import { useEffect, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { useMediaPredicate } from 'react-media-hook';
import { MOBILE_SIZE } from 'utils/mediaQueryUtils';

import Spinner from 'components/Spinner';
import { usePageTitle } from 'utils/pageTitle';
import { decodeAddressParam } from 'utils/addressUtils';
import { fetchFirstLoadTasks } from 'reducers/firstLoadTasksReducer';
import {
	checkAddressAsString,
	checkBlockAndLot,
	checkByBin,
} from 'data/reducers/addressReducer';
import { fetchZoningData } from 'reducers/zoningReducer';
import { actions } from 'data/store';
import { dataConfig } from 'data/dataConfig';
import { getReducerName } from 'data/dataSourceConstants';
import { showToast } from 'reducers/toastReducer';

import OverviewToolbar from 'pages/_rd_overview/toolbar/OverviewToolbar';

import PenaltiesRefundsCardV2 from 'pages/_rd_overview/PenaltiesRefundsCardV2';
import AlertsCard from 'pages/_rd_overview/AlertsCard';
import MapCard from 'pages/_rd_overview/mapCard/MapCard';
import BuildingInfoCard from 'pages/_rd_overview/propertyInfoCard/BuildingInfoCard';
import DataSetsCardVertical from 'pages/_rd_overview/dataSetsCard/DataSetsCardVertical';
import DataSetsCardMobile from 'pages/_rd_overview/dataSetsCard/DataSetsCardMobile';
import VendorsCard from 'pages/_rd_overview/VendorsCard';

import './PropertyOverview.css';

const PropertyOverview = (props) => {
	const dispatch = useDispatch();
	let { address } = useParams();
	const location = useLocation();
	const isMobile = useMediaPredicate(MOBILE_SIZE);

	// Track the last route address we dispatched for to avoid infinite re-dispatch loops
	const lastCheckedRef = useRef(null);

	let { isLoading, propertyData, propertyId, zoningData } = useSelector(
		(state) => ({
			// Only block on property loading to avoid UI being stuck due to address/zoning ancillary states
			isLoading: state.property.isLoading,
			propertyData: state.property.propertyData,
			propertyId: state.property.propertyData?.id,
			zoningData: state.zoning.data || {},
		}),
	);

	// Only truthy when firstLoadTasks data belongs to the current property (avoids stale data from previous navigation)
	let firstLoadTasksData = useSelector((state) =>
		state.firstLoadTasks?.propertyId === propertyId
			? state.firstLoadTasks?.data
			: null,
	);
	const firedForPropertyRef = useRef(null);

	usePageTitle(null, propertyData);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		const params = new URLSearchParams(location.search || '');
		const viaAka = params.get('aka') === '1';
		const block = params.get('block');
		const lot = params.get('lot');
		const borough = params.get('borough');
		const bin = params.get('bin');

		// Prefer BIN resolution first when AKA is used and BIN is present, especially if it differs from current BIN
		if (viaAka && bin && bin !== String(propertyData?.bin || '')) {
			const key = `bin:${bin}`;
			if (lastCheckedRef.current !== key) {
				lastCheckedRef.current = key;
				dispatch(checkByBin(bin));
			}
			return; // Prefer BIN path; avoid immediate BBL dispatch when BIN differs
		}

		// Prefer block/lot resolution when coming from an AKA link and identifiers are present (and BIN is not provided or matches current)
		if (viaAka && block && lot) {
			const key = `b${block}-l${lot}`;
			if (lastCheckedRef.current !== key) {
				lastCheckedRef.current = key;
				dispatch(checkBlockAndLot({ block, lot, borough }));
			}
			return;
		}

		// BIN-only fallback via identifiers endpoint
		if (viaAka && bin && !(block && lot)) {
			const key = `bin:${bin}`;
			if (lastCheckedRef.current !== key) {
				lastCheckedRef.current = key;
				dispatch(checkByBin(bin));
			}
			// Do not return; we still want address fallback if BIN endpoint yields nothing
		}
	}, [address, location.search, dispatch, propertyData?.address]);

	useEffect(() => {
		let decodedAddress = decodeAddressParam(address);
		if (!isLoading && decodedAddress !== propertyData?.address) {
			dispatch(checkAddressAsString(decodedAddress));
		}
	}, [address]);

	useEffect(() => {
		if (propertyId) {
			dispatch(fetchFirstLoadTasks(propertyId));
			dispatch(fetchZoningData(propertyId));
		}
	}, [propertyId]);

	// Once firstLoadTasks arrives, call fetchData for every in-progress dataset (parallel via browser)
	useEffect(() => {
		if (!firstLoadTasksData || !propertyId) return;
		if (firedForPropertyRef.current === propertyId) return;

		firedForPropertyRef.current = propertyId;

		const timerId = setTimeout(() => {
			firstLoadTasksData
				.filter((task) => !task.complete && !task.errored_at)
				.forEach((task) => {
					for (const [, config] of Object.entries(dataConfig)) {
						if (!config.firstLoadTaskNames) continue;
						for (const [dataSource, name] of Object.entries(
							config.firstLoadTaskNames,
						)) {
							if (name === task.task_name) {
								const reducerName = getReducerName(config, dataSource);
								if (actions[reducerName]?.fetchData) {
									dispatch(actions[reducerName].fetchData(propertyId, 'open'));
								}
							}
						}
					}
				});
		}, 2000);

		return () => clearTimeout(timerId);
	}, [firstLoadTasksData, propertyId, dispatch]);

	useEffect(() => {
		if (propertyData?.started_new_scrape) {
			dispatch(
				showToast({
					title: 'Fetching Property Data',
					body: "We're downloading the most up to date data for this property. This might take a couple minutes.",
				}),
			);
		}
	}, [propertyData?.started_new_scrape]);

	// TEMP: Disable loader to unblock UI while debugging API/state flow
	if (isLoading) {
		return <Spinner />;
	}

	return (
		<div className="rd-overview-page">
			<OverviewToolbar />

			<div className="rd-overview-container">
				{/* Top 2-Column Section */}
				<div className="overview-main-columns">
					<div className="overview-left-column">
						<BuildingInfoCard
							key={propertyId || address}
							propertyData={propertyData}
							zoningData={zoningData}
						/>
					</div>
					<div className="overview-right-column">
						<AlertsCard />
						<PenaltiesRefundsCardV2 />
					</div>
				</div>

				{/* Property Images Section */}
				<div className="overview-images-row">
					<MapCard
						key={`map-${propertyId || address}`}
						propertyData={propertyData}
						zoningData={zoningData}
					/>
				</div>

				{/* Datasets Section */}
				<div className="overview-datasets-row mt-4">
					{isMobile ? <DataSetsCardMobile /> : <DataSetsCardVertical />}
				</div>

				{/* Vendors Section */}
				<div className="overview-vendors-row">
					<VendorsCard />
				</div>

				<div className="overview-footer">
					<Link to="/use-policy">Acceptable Use Policy</Link>
					<span className="overview-footer-sep">|</span>
					<Link to="/disclaimer">Disclaimer</Link>
				</div>
			</div>
		</div>
	);
};

export default PropertyOverview;
